import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { initAnalytics, track } from "./analytics/posthog";
import { initMonitoring } from "./monitoring/sentry";
import { createSupabaseProviders } from "@retreats/supabase-adapter";
import { AuthProvider } from "./auth/authContext";
import { isE2E, isE2ESilent } from "./utils/env";
import {
  RetreatManager,
  VoiceAlertPlane,
  RetreatState,
} from "@retreats/engine";
import {
  ObjectiveClock,
  IndexedDBProvider,
  LocalStorageProvider,
} from "@retreats/runtime-adapter";
import { PersistenceCoordinator } from "@retreat/core";
import { audioPlane, textPlane } from "./planes";
import { AudioContextWatcher } from "./audio/AudioContextWatcher";
import type {
  IAuthProvider,
  ILicenseProvider,
} from "@retreat/platform-contracts";

// Mock providers for e2e tests
class MockAuthProvider implements IAuthProvider {
  async signInWithMagicLink(_email: string): Promise<void> {}
  async signInWithGoogle(): Promise<void> {}
  async signOut(): Promise<void> {}
  async getSession() {
    return { userId: "e2e-test-user", email: "test@example.com" };
  }
  onAuthStateChange(callback: (session: any) => void) {
    callback({ userId: "e2e-test-user", email: "test@example.com" });
    return () => {};
  }
}

class MockLicenseProvider implements ILicenseProvider {
  async checkLicense(_userId: string) {
    return { active: true, activatedAt: new Date().toISOString() };
  }
  async startCheckout(_inviteCode: string) {
    return { checkoutUrl: "https://example.com/checkout" };
  }
  async redeemToken(_token: string) {
    return { success: true };
  }
}


// Init observability before anything else so errors during boot are captured
initMonitoring();
initAnalytics();
track("app_loaded", { app_version: __APP_VERSION__ });

// Request persistent storage so IndexedDB sessions aren't evicted under mobile storage pressure.
// navigator.storage.persist() only needs to be called once; the browser remembers the grant.
if (navigator.storage?.persist) {
  navigator.storage.persist().catch(() => {});
}

// E2E silent mode: set before engine init so SystemAlertManager.initialize() sees it.
// Usage: VITE_E2E_SILENT=1 npm run test:e2e  (or ci:local sets it via ci-local.js)
if (import.meta.env?.VITE_E2E_SILENT === "1") {
  window.__E2E_SILENT__ = true;
}

// Expand Roarr logs in console for debugging (DEV only — pretty-print JSON objects)
if (import.meta.env.DEV) {
  const originalLog = console.log;
  console.log = (...args) => {
    originalLog(
      ...args.map((arg) =>
        typeof arg === "object" ? JSON.stringify(arg, null, 2) : arg,
      ),
    );
  };
}

// Initialize the engine singleton at module scope (Phase 1)
// Guard against re-initialization on page reload (e.g., in e2e tests)
const clock = new ObjectiveClock(16);
const coordinator = new PersistenceCoordinator([
  new IndexedDBProvider(),
  new LocalStorageProvider(),
]);
try {
  RetreatManager.initialize(clock, coordinator, isE2ESilent());
} catch (err) {
  if (
    err instanceof Error &&
    err.message.includes("Singleton already initialized")
  ) {
    // Already initialized from a previous module load — this is expected in e2e tests
    // that reload the page. The singleton persists across reloads.
  } else {
    throw err;
  }
}

// Register execution planes (created at module scope in planes.ts)
const manager = RetreatManager.get();
const voicePlane = new VoiceAlertPlane(manager.getSystemAlertManager());
manager.registerPlane(audioPlane);
manager.registerPlane(textPlane);
manager.registerPlane(voicePlane); // M2-ENGINE-P0-008: bells have priority over announcements

// M2-ENGINE-P0-009: iOS Background Audio Keep-Alive
const audioWatcher = new AudioContextWatcher(
  () => audioPlane.getAudioContext(),
  () => manager.getSnapshot().status === RetreatState.RUNNING,
);
audioWatcher.start();

// M2-ENGINE-P0-007: MediaSession API — handled by useMediaSession() hook in RetreatBridge

// Lifecycle listeners (online, visibilitychange → drain) are registered by
// useLifecycleBoot via syncManager.init() after the React tree mounts.

// ── Story capture ────────────────────────────────────────────────────────────
// Three sources feed the unified event stream in uiLog:
//   1. DOM click listener → logClick (every button/link tap with visible label)
//   2. ROARR.write tap → logRoarrEntry (engine internals: bells, session advances)
//   3. Engine subscribe → logEngineTransition (status changes: IDLE → RUNNING)
//
// Call window.downloadTrace() in the browser console at any point.
import { getStory, logRoarrEntry, addStoryMarker, removeStoryEntry, clearUILog } from "./debug/uiLog";
import { captureStateSnapshot } from "./debug/state-snapshot";

// Shared formatting — used by both downloadTrace and printStory
const KIND_LABELS: Record<string, string> = {
  "screen-render": "RENDER",
  "click":         "TAP",
  "engine":        "ENGINE",
  "ui-action":     "ACTION",
  "engine-log":    "LOG",
  "marker":        "MARK",
};

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, "0"))
    .join(":") + "." + String(d.getMilliseconds()).padStart(3, "0");
}

function isSensitiveKey(key: string): boolean {
  return key.startsWith("sb-") || key.includes("auth-token") || key.includes("access_token");
}

// 2. ROARR.write interception — tap into the write pipeline before it hits console.log.
//    Roarr is always enabled in the browser (logger.ts line 50) at INFO+ level.
const _originalRoarrWrite = (globalThis as any).ROARR?.write as
  | ((msg: string) => void)
  | undefined;
(globalThis as any).ROARR ??= {};

// Intercept Roarr logs to capture engine events for the trace
// Re-entrancy guard: logRoarrEntry emits through Roarr itself, which would re-trigger this handler
let _isCapturingRoarr = false;

(globalThis as any).ROARR.write = (message: string) => {
  if (!_isCapturingRoarr) {
    _isCapturingRoarr = true;
    try {
      const parsed = JSON.parse(message) as {
        message: string;
        context?: Record<string, unknown>;
      };
      const level = (parsed.context?.logLevel as number) ?? 30;
      if (level >= 30 && parsed.context?.namespace !== "ui") {
        logRoarrEntry(parsed.message, parsed.context);
      }
    } catch {
      // non-JSON — pass through
    } finally {
      _isCapturingRoarr = false;
    }
  }
  _originalRoarrWrite?.(message);
};

// 3. Engine state transition history — handled by useEngineTransitionLog() hook in RetreatBridge.

// Helper to download a file
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

if (import.meta.env.DEV) {
// 4. downloadTrace — two files: NDJSON (machine-readable) + TXT (human-readable)
//    async because IndexedDB read (session history) is async.
//    Usage: window.downloadTrace()
window.downloadTrace = async () => {
  const events = getStory();
  const engineSnap = manager.getSnapshot();
  const ts = Date.now();

  // ── File 1: NDJSON — one Roarr-compatible JSON object per line ──────────
  const ndjson = events
    .map((e, i) =>
      JSON.stringify({
        sequence: i + 1,
        time: e.iso,
        message: e.label,
        context: {
          namespace: e.kind === "engine-log" ? (e.detail?.namespace ?? e.detail?.manager ?? "engine") : "ui",
          kind: e.kind,
          screen: e.screen,
          logLevel: 30,
          ...e.detail,
        },
      }),
    )
    .join("\n");

  downloadFile(ndjson, `retreat-trace-${ts}.ndjson`, "application/x-ndjson");

  // ── File 2: TXT — human-readable, pipe-aligned, no emojis ─────────────
  const stateSnap = await captureStateSnapshot("trace-download").catch(
    () => ({ indexedDB: {} as Record<string, unknown> }),
  );

  const clickCount = events.filter((e) => e.kind === "click").length;
  const renderCount = events.filter((e) => e.kind === "screen-render").length;
  const engineCount = events.filter((e) => e.kind === "engine").length;

  const timeline = events.map((e, i) => {
    const kind = (KIND_LABELS[e.kind] ?? e.kind).padEnd(8).slice(0, 8);
    const screen = e.screen.padEnd(16);
    const detail = e.detail
      ? " | " + Object.entries(e.detail)
          .filter(([, v]) => v !== null && v !== undefined)
          .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
          .join(" ")
      : "";
    return `[${String(i + 1).padStart(3)}] ${formatTimestamp(e.ts)}  ${screen}  ${kind}  ${e.label}${detail}`;
  });

  const idbSessions = Object.entries(stateSnap.indexedDB ?? {});

  const txt = [
    "RETREAT CONDUCTOR -- SESSION TRACE",
    "=".repeat(72),
    "",
    `Captured : ${new Date().toISOString()}`,
    `Engine   : ${engineSnap.status}  elapsed: ${engineSnap.elapsedSeconds}s`,
    `Events   : ${events.length} total  (${renderCount} renders  ${clickCount} taps  ${engineCount} engine transitions)`,
    "",
    "TIMELINE",
    "-".repeat(72),
    " idx  hh:mm:ss.ms  screen            kind      label",
    "-".repeat(72),
    ...timeline,
    "",
    "STORAGE (localStorage)",
    "-".repeat(72),
    ...(() => {
      try {
        return Object.entries(localStorage)
          .filter(([k]) => !isSensitiveKey(k))
          .map(([k, v]) => `  ${k}: ${v}`);
      } catch {
        return ["  (unavailable)"];
      }
    })(),
    "",
    "SESSION HISTORY (IndexedDB)",
    "-".repeat(72),
    ...(idbSessions.length
      ? idbSessions.map(([k, v]) => `  ${k}: ${JSON.stringify(v)}`)
      : ["  (no sessions recorded yet)"]),
  ].join("\n");

  downloadFile(txt, `retreat-trace-${ts}.txt`, "text/plain");

  console.log(`[trace] Downloaded ${events.length} events -> retreat-trace-${ts}.ndjson + .txt`);
};

// Keep old name as alias
window.downloadStory = window.downloadTrace;

// Quick console view — prints table with index numbers
window.printStory = () => {
  console.group(`Session trace (${getStory().length} events)`);
  getStory().forEach((e, i) => {
    const k = KIND_LABELS[e.kind] ?? e.kind;
    const line = `[${i}] ${formatTimestamp(e.ts)}  [${e.screen}]  ${k}: ${e.label}`;
    if (e.detail) {
      console.log(line, e.detail);
    } else {
      console.log(line);
    }
  });
  console.groupEnd();
};

window.markStory = (label: string) => addStoryMarker(label);
window.removeStoryEntry = (index: number) => removeStoryEntry(index);
window.clearStory = () => {
  clearUILog();
  console.log("[trace] Story cleared.");
};
} // end if (import.meta.env.DEV)

// Create auth/license providers — single instance for the app lifetime
let auth: IAuthProvider;
let license: ILicenseProvider;

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (isE2E() || !supabaseUrl || !supabaseAnonKey) {
  // E2E tests, or build without env vars (CI preview, local build without .env.local).
  // Auth and licensing are disabled — the core retreat engine still works.
  auth = new MockAuthProvider();
  license = new MockLicenseProvider();
} else {
  const providers = createSupabaseProviders({ url: supabaseUrl, anonKey: supabaseAnonKey });
  auth = providers.auth;
  license = providers.license;
}

// StrictMode only in dev - it double-mounts to catch bugs
// In production, single mount only
const root = import.meta.env.DEV ? (
  <StrictMode>
    <ErrorBoundary>
      <AuthProvider auth={auth} license={license}>
        <App />
      </AuthProvider>
    </ErrorBoundary>
  </StrictMode>
) : (
  <ErrorBoundary>
    <AuthProvider auth={auth} license={license}>
      <App />
    </AuthProvider>
  </ErrorBoundary>
);

createRoot(document.getElementById("root")!).render(root);
